/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import ErrorLevel from '../error/error-level';

/**
 * Zaznamenávač výjimek pro Console API.
 * Vypisuje chyby do vývojářské konzole v prohlížeči.
 * @implements {Logger}
 */
class ConsoleLogger {
  /**
   * Vytvoří instanci.
   * @param {Console} console
   * @param {ErrorLevelFactory} errorLevelFactory
   */
  constructor(console, errorLevelFactory) {
    this.console = console;
    this.errorLevelFactory = errorLevelFactory;
  }

  /**
   * Zaznamená libovolnou hodnotu,
   * přičemž se hodnota úrovně chyby použije primárně z této hodnoty
   * a sekundárně z názvu metody zanamenávače chyb,
   * přes kterou byla chyba předána.
   * @param {ErrorLevel} methodLevel Úroveň chyby, která přísluší metodě,
   * která byla na zaznamenávači výjimek volána.
   * @param {*} value Libovolná hodnota k zalogování.
   * @private
   * @returns {undefined}
   */
  capture(methodLevel, value) {
    const { console } = this;
    const valueLevel = this.errorLevelFactory.fromValue(value);
    const level = valueLevel || methodLevel;
    switch (level) {
      case ErrorLevel.Debug:
        return console.debug(value);
      case ErrorLevel.Error:
        return console.error(value);
      case ErrorLevel.Info:
        return console.info(value);
      case ErrorLevel.Log:
        return console.log(value);
      case ErrorLevel.Warning:
        return console.warn(value);
      default:
        throw new RangeError(`Unknown level "${level}".`);
    }
  }

  /** @inheritdoc */
  debug(value) {
    return this.capture(ErrorLevel.Debug, value);
  }

  /** @inheritdoc */
  error(value) {
    return this.capture(ErrorLevel.Error, value);
  }

  /** @inheritdoc */
  info(value) {
    return this.console.info(value);
  }

  /** @inheritdoc */
  log(value) {
    return this.capture(ErrorLevel.Log, value);
  }

  /** @inheritdoc */
  setInfo(loggerInfo) {
    // Prázdná metoda pro splnění rozhraní.
    // Do vývojářské konzole se žádné dodatečné informace nevypisují.
  }

  /** @inheritdoc */
  warn(value) {
    return this.capture(ErrorLevel.Warning, value);
  }
}

export default ConsoleLogger;
